/* eslint-disable no-unused-vars */
import {
  ForwardedRef,
  forwardRef,
  HTMLAttributes,
  VideoHTMLAttributes
} from 'react'
import NextImage, { ImageProps as NextImageProps } from 'next/image'

import useStableViewport from '@/hooks/use-stable-viewport'
import { useLoaderStore } from '@/store/loader'

import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

export type ImageProps = Omit<NextImageProps, 'src' | 'alt'> &
  Partial<{
    source: WPMedia
    size: 'thumbnail' | 'medium' | 'large' | 'full'
    fit: 'contain' | 'cover'
    alt: string
    priority: boolean
    // eslint-disable-next-line no-unused-vars
    onMediaReady: () => void
    parentProps: HTMLAttributes<HTMLDivElement> & {
      [key: `data-${string}`]: string | number | boolean | undefined
    }
  }>

export type VideoProps = VideoHTMLAttributes<HTMLVideoElement> &
  Partial<{
    source: WPMedia
    fit: 'contain' | 'cover'
    fill: boolean
    // eslint-disable-next-line no-unused-vars
    onMediaReady: () => void
    parentProps: HTMLAttributes<HTMLDivElement> & {
      [key: `data-${string}`]: string | number | boolean | undefined
    }
  }>

export type MediaProps = {
  media: WPMedia
}

const hasValue = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const getNumericValue = (value: unknown) => {
  const parsedValue = Number(value)

  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : undefined
}

const getImageAsset = (
  image: WPMedia['poster'] | undefined,
  size: ImageProps['size']
) => {
  const sizedAsset = size && size !== 'full' ? image?.sizes?.[size] : undefined
  const hasSizedUrl = hasValue(sizedAsset?.url)

  return {
    url: hasSizedUrl ? sizedAsset?.url : image?.url || '',
    width: hasSizedUrl
      ? (getNumericValue(sizedAsset?.width) ?? getNumericValue(image?.width))
      : getNumericValue(image?.width),
    height: hasSizedUrl
      ? (getNumericValue(sizedAsset?.height) ?? getNumericValue(image?.height))
      : getNumericValue(image?.height),
    alt: hasValue(image?.alt) ? image.alt : ''
  }
}

const hasImageAsset = (
  image: WPMedia['poster'] | undefined,
  size: ImageProps['size']
) => {
  const asset = getImageAsset(image, size)

  return hasValue(asset.url)
}

const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      source,
      fit = 'cover',
      fill = true,
      size = 'full',
      className,
      onLoad,
      onError,
      alt,
      priority,
      onMediaReady,
      parentProps,
      ...props
    },
    ref
  ) => {
    const { isMobile } = useStableViewport()

    const resolvedSource =
      isMobile && hasImageAsset(source?.poster_mobile, size)
        ? source?.poster_mobile
        : source?.poster

    const imageAsset = getImageAsset(resolvedSource, size)

    return (
      <div
        className={cn('relative h-full w-full overflow-hidden', className)}
        {...parentProps}
      >
        <NextImage
          ref={ref}
          src={imageAsset.url}
          alt={alt ?? imageAsset.alt}
          className={cn(
            fit === 'contain' ? 'object-contain' : 'object-cover',
            fill ? 'absolute h-full w-full' : 'h-auto w-full'
          )}
          {...props}
          width={imageAsset.width}
          height={imageAsset.height}
          onLoad={(event) => {
            onLoad?.(event)
            onMediaReady?.()
          }}
          onError={(event) => {
            onError?.(event)
            onMediaReady?.()
          }}
          priority={priority}
        />
      </div>
    )
  }
)

const Video = forwardRef<HTMLVideoElement, VideoProps>(
  (
    {
      source,
      fit = 'cover',
      fill = true,
      className,
      autoPlay = true,
      muted = true,
      loop = true,
      playsInline = true,
      controls = false,
      onCanPlay,
      onLoadedData,
      onError,
      onMediaReady,
      parentProps,
      ...props
    },
    ref
  ) => {
    const { isMobile } = useStableViewport()

    return (
      <div
        className={cn('relative h-full w-full overflow-hidden', className)}
        {...parentProps}
      >
        <video
          ref={ref}
          src={
            isMobile && source?.video_mobile?.url
              ? source?.video_mobile?.url
              : source?.video?.url
          }
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          controls={controls}
          onCanPlay={(event) => {
            onCanPlay?.(event)
            onMediaReady?.()
          }}
          onLoadedData={(event) => {
            onLoadedData?.(event)
            onMediaReady?.()
          }}
          onError={(event) => {
            onError?.(event)
            onMediaReady?.()
          }}
          className={cn(
            fit === 'contain' ? 'object-contain' : 'object-cover',
            fill ? 'absolute h-full w-full' : 'h-auto w-full'
          )}
          {...props}
        />
      </div>
    )
  }
)

Video.displayName = 'Video'

export const hasVideo = (media: WPMedia) => {
  return !!(media.video?.url || media.video_mobile?.url)
}

export const hasMedia = (media: WPMedia) => {
  return hasVideo(media) || hasImageAsset(media.poster, 'full')
}

const Media = forwardRef<
  HTMLDivElement,
  { media: WPMedia; unlockLoaderOnReady?: boolean } & Partial<
    ImageProps & VideoProps
  >
>(
  (
    {
      media,
      className,
      onLoad,
      priority,
      unlockLoaderOnReady = false,
      ...props
    },
    ref
  ) => {
    const setLoaderState = useLoaderStore((state) => state.setLoaderState)
    if (!media) return null

    const handleMediaReady = () => {
      if (unlockLoaderOnReady) {
        setLoaderState({ heroLoaded: true })
      }
    }

    return hasVideo(media) ? (
      <Video
        data-video
        source={media}
        className={className}
        onLoad={onLoad}
        onMediaReady={handleMediaReady}
        ref={ref as ForwardedRef<HTMLVideoElement>}
        {...props}
      />
    ) : (
      // eslint-disable-next-line jsx-a11y/alt-text
      <Image
        data-image
        source={media}
        className={className}
        onLoad={onLoad}
        onMediaReady={handleMediaReady}
        ref={ref as ForwardedRef<HTMLImageElement>}
        priority={priority}
        {...props}
      />
    )
  }
)

Media.displayName = 'Media'

export { Image, Video }
export default Media
